import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { getFramework, getTrackFunc, type FormatBrushImplementation, type IFramework } from '@/framework';
import type { EdgelessFocusMaskManager } from '@/plugins/apps/ai-agent/edgeless-focus-mask';
import { base64Kit } from '@/utils/base64-kit';
import { glKit } from '@/utils/gl-kit';
import { isEmptyEffect } from '@/utils/layer-kit';
import { markLayerOutputDraft, setLayerOutputSourceCanvas } from '@/utils/layer-output-draft-kit';
import { LayerType, type IImageLayerProps, type ILayerProps } from '@/version-types';
import type { ImageLayer } from '../..';
import { renderImageBaseLayerCanvas } from '../../render/render-to-canvas';
import { applyImageBrushEffect, type ImageBrushEffectData } from './format-brush';
import ImageEffectAction, { getEffectParams, type IImageEffectPreviewLease } from './runtime/image-effect-action';
import { getImageEffectState, setCurrentLayer, watchImageEffectState, type IStore } from './states/image-effect-state';

type ClassicBlurParams = Record<string, number>;

const DefaultClassicBlurParams: ClassicBlurParams = {
    mModelType: 2, // 自动识别焦点
    mUpdateEffect: 1, // 每次切换都更新特效
    mFocalScale: 0.5, // 取值范围 0-1
};

/** 图片特效草稿更新选项。 */
export interface IUpdateImageEffectDraftOptions {
    /** 应用时写入历史的统计标识。 */
    statTrackKey?: string;
    /** 明确表示草稿已经清空特效，用于同步清理派生产物。 */
    isEmpty?: boolean;
}

/** 图片本体模糊类型。 */
export type IImageBlurType = 'classic' | 'glass';

/** 图片特效会话取消控制项 */
export interface ICancelImageEffectOptions {
    /** 恢复原图后是否重新激活该图层，默认 false */
    activateLayer?: boolean;
}

interface IPendingImageEffectApply {
    canCommit: boolean;
    originLayerId: string;
}

interface ILatestImageEffectDraft {
    layer: ILayerProps;
    options: IUpdateImageEffectDraftOptions;
    sessionGeneration: number;
}

interface IBlurDraft {
    layerId: string;
    sourceCanvas: HTMLCanvasElement;
    type: IImageBlurType;
}

interface IEdgelessImageLayerObject {
    setPreviewLayer?(layer: ILayerProps, token?: symbol): Promise<HTMLCanvasElement | null>;
    clearPreviewLayer?(rerender?: boolean): void;
    setEffectPreviewLayer?(
        token: symbol,
        layer: ILayerProps,
        effectUnderlayCanvas: HTMLCanvasElement,
    ): Promise<HTMLCanvasElement | null>;
    clearEffectPreviewLayer?(token: symbol, rerender?: boolean): boolean;
    setResource?(source: HTMLCanvasElement): void | Promise<void>;
    clearPreviewResource?(options?: { rerender?: boolean }): void;
    commitAppliedResource?(): void;
}

export class ImageEffectPlugin {
    /** 编辑器实例 */
    private framework: IFramework;
    private store: IStore;
    private panel: React.ReactNode = null;
    private renderTaskId = 0;
    private sessionGeneration = 0;
    private pendingApplies = new Map<number, IPendingImageEffectApply>();
    private latestDraft: ILatestImageEffectDraft | null = null;
    private effectPreviewToken: symbol | null = null;
    private effectPreviewLease: IImageEffectPreviewLease | null = null;
    private focusToken: symbol | null = null;
    private classicBlurParams: ClassicBlurParams = cloneDeep(DefaultClassicBlurParams);
    private effectData: ImageBrushEffectData | null = null;
    private blurDraft: IBlurDraft | null = null;
    private blurRenderTaskId = 0;

    constructor(identity: string) {
        this.framework = getFramework(identity);
        this.store = getImageEffectState(this.framework.identity);

        watchImageEffectState(identity, (store) => {
            this.store = store;
        });
    }

    setPanel(panel: React.ReactNode) {
        this.panel = panel;
    }

    getPanel() {
        return this.panel;
    }

    getState() {
        return this.store;
    }

    /**
     * 把最后一个成功显示的基础特效草稿投影到输出副本
     *
     * 渲染中的 latestDraft 不参与输出，避免保存屏幕尚未显示的候选结果。
     *
     * @param layer - 保存或下载链路创建的 ImageLayer 副本
     * @returns 成功投影稳定草稿时返回 true
     * 副作用：仅更新传入图层，不提交正式图层或改变特效会话。
     */
    projectOutputDraft(layer: ILayerProps) {
        const { originLayer, currentLayer } = this.syncStore();
        let projected = false;
        if (originLayer && currentLayer && originLayer.id === layer.id && currentLayer.id === layer.id) {
            const projectedLayer = this.mergeDraftIntoLatest(layer, currentLayer);
            const projectedData = projectedLayer.data as IImageLayerProps;
            const layerData = layer.data as IImageLayerProps;
            layer.opacity = projectedLayer.opacity;
            layerData.effect = cloneDeep(projectedData.effect);
            if (projectedData.borderRadius === undefined) {
                delete layerData.borderRadius;
            } else {
                layerData.borderRadius = cloneDeep(projectedData.borderRadius);
            }
            if (layerData.effect) layerData.effect.screenshotUrl = '';
            markLayerOutputDraft(layer);
            projected = true;
        }

        if (this.blurDraft?.layerId === layer.id) {
            setLayerOutputSourceCanvas(layer, this.blurDraft.sourceCanvas);
            const effect = (layer.data as IImageLayerProps).effect;
            if (effect) {
                effect.screenshotUrl = '';
                delete effect.effectPosition;
            }
            projected = true;
        }
        if (projected) this.cleanRenderFlags(layer);
        return projected;
    }

    private syncStore() {
        this.store = getImageEffectState(this.framework.identity);
        return this.store;
    }

    private cleanRenderFlags(layer: ILayerProps) {
        glKit.clearGL(layer);
        delete layer.__isImageEffectProcessed;
        return layer;
    }

    private getTrackFunc(layer: ILayerProps, options?: IUpdateImageEffectDraftOptions) {
        const statTrackKey = options?.statTrackKey;
        return statTrackKey ? getTrackFunc(this.framework.identity, layer, statTrackKey) : undefined;
    }

    private getLayerObject(layerId: string) {
        const { edgelessCanvas } = this.framework.service;
        return edgelessCanvas.getByLayerId(layerId) as unknown as IEdgelessImageLayerObject | undefined;
    }

    private getFocusManager() {
        return this.framework.service.registration.getInstance<EdgelessFocusMaskManager>('EdgelessFocusMask');
    }

    private acquireFocus(layerId: string) {
        if (!this.framework.service.edgelessCanvas.isEdgelessMode || this.focusToken) return;
        const manager = this.getFocusManager();
        this.focusToken = manager?.acquireFocus?.(layerId, { blindAreas: { right: 276 + 16 } }) ?? null;
    }

    private releaseFocus() {
        if (!this.focusToken) return;
        this.getFocusManager()?.releaseFocus?.(this.focusToken);
        this.focusToken = null;
    }

    private clearPreview(layerId: string, token = this.effectPreviewToken, rerender = true) {
        const layerObject = this.getLayerObject(layerId);
        if (token && layerObject?.clearEffectPreviewLayer) {
            layerObject.clearEffectPreviewLayer(token, rerender);
            return;
        }
        layerObject?.clearPreviewLayer?.(rerender);
    }

    private releaseEffectPreview(layerId?: string, rerender = true) {
        const token = this.effectPreviewToken;
        ImageEffectAction.releasePreviewLease(this.effectPreviewLease);
        this.effectPreviewLease = null;
        this.effectPreviewToken = null;
        if (layerId && token) {
            this.clearPreview(layerId, token, rerender);
        }
    }

    private cleanEffectArtifact(layer: ILayerProps) {
        const cleanLayer = this.cleanRenderFlags(cloneDeep(layer));
        const effect = (cleanLayer.data as IImageLayerProps).effect;
        if (effect) {
            effect.screenshotUrl = '';
            delete effect.effectPosition;
        }
        return cleanLayer;
    }

    private mergeDraftIntoLatest(latestLayer: ILayerProps, draftLayer: ILayerProps) {
        const mergedLayer = this.cleanRenderFlags(cloneDeep(latestLayer));
        const mergedData = mergedLayer.data as IImageLayerProps;
        const draftData = draftLayer.data as IImageLayerProps;
        mergedLayer.opacity = draftLayer.opacity;
        mergedData.effect = cloneDeep(draftData.effect);
        if (draftData.borderRadius === undefined) {
            delete mergedData.borderRadius;
        } else {
            mergedData.borderRadius = cloneDeep(draftData.borderRadius);
        }
        return mergedLayer;
    }

    private getImageLayer() {
        return this.framework.service.registration.getInstance<ImageLayer>('ImageLayer');
    }

    /**
     * 打开图片特效编辑会话
     *
     * @param layerId - 图片图层 ID
     */
    open(layerId: string) {
        const { flatLayer, frame } = this.framework.service;

        const originIndex = frame.getActiveFrameId();
        const originLayer = layerId ? flatLayer.getLayer(layerId) : frame.getMainLayer(originIndex);

        if (!originLayer) return;
        const url = (originLayer.data as IImageLayerProps).url;
        if (!url) return;

        const previousOriginLayer = this.syncStore().originLayer;
        if (previousOriginLayer) {
            this.pendingApplies.forEach((pendingApply) => {
                if (pendingApply.originLayerId === previousOriginLayer.id) {
                    pendingApply.canCommit = false;
                }
            });
            this.releaseEffectPreview(previousOriginLayer.id);
        } else {
            this.releaseEffectPreview(undefined, false);
        }
        if (previousOriginLayer && previousOriginLayer.id !== originLayer.id) {
            this.releaseFocus();
        }
        const newLayer = cloneDeep(originLayer);

        this.pendingApplies.forEach((pendingApply) => {
            if (pendingApply.originLayerId === originLayer.id) {
                pendingApply.canCommit = false;
            }
        });
        this.renderTaskId += 1;
        this.sessionGeneration += 1;
        this.effectPreviewToken = Symbol(`image-effect-preview:${originLayer.id}`);
        this.latestDraft = {
            layer: cloneDeep(newLayer),
            options: {},
            sessionGeneration: this.sessionGeneration,
        };
        this.store.init(cloneDeep(originLayer), newLayer);
        this.syncStore().setIsWasmReady(true);
        this.acquireFocus(originLayer.id);
    }

    showOriginLayer() {
        const originLayer = this.store.originLayer;
        if (!originLayer) return;
        this.clearPreview(originLayer.id);
    }

    private finishSession(originLayer: ILayerProps, sessionGeneration: number, clearPreview: boolean) {
        const ownsCurrentSession = sessionGeneration === this.sessionGeneration;
        if (!ownsCurrentSession) return;
        const previewToken = this.effectPreviewToken;
        this.sessionGeneration += 1;
        this.renderTaskId += 1;
        this.latestDraft = null;
        ImageEffectAction.releasePreviewLease(this.effectPreviewLease);
        this.effectPreviewLease = null;
        this.effectPreviewToken = null;
        this.store.destroy();
        this.releaseFocus();
        if (clearPreview) {
            this.clearPreview(originLayer.id, previewToken);
        }
    }

    private restoreOriginLayer(
        originLayer: ILayerProps,
        activateLayer: boolean,
        sessionGeneration: number,
        shouldRestore: boolean,
    ) {
        if (shouldRestore) {
            if (activateLayer) {
                this.framework.service.frame.setActiveLayer(originLayer.id);
            }
        }
        this.finishSession(originLayer, sessionGeneration, shouldRestore);
    }

    watchState(callback: (state: IStore) => void) {
        return watchImageEffectState(this.framework.identity, callback);
    }

    /** 关闭图片特效编辑会话。 */
    close() {
        this.cancel();
    }

    /** 把当前特效草稿重置为会话打开时的原图层。 */
    reset() {
        const originLayer = this.store.originLayer;

        if (!originLayer) {
            return;
        }
        const layer = this.cleanRenderFlags(cloneDeep(originLayer));
        void this.updateDraft(layer, { isEmpty: isEmptyEffect(layer.data as IImageLayerProps) });
    }

    setCurrentLayer(layer: ILayerProps) {
        setCurrentLayer(this.framework.identity, layer);
        this.latestDraft = {
            layer: cloneDeep(layer),
            options: {},
            sessionGeneration: this.sessionGeneration,
        };
    }

    async updateDraft(layer: ILayerProps, options: IUpdateImageEffectDraftOptions = {}) {
        const sessionGeneration = this.sessionGeneration;
        const originLayer = this.syncStore().originLayer;
        if (!originLayer || originLayer.id !== layer.id) return;

        const taskId = ++this.renderTaskId;
        const draftLayer = cloneDeep(layer);
        this.latestDraft = {
            layer: cloneDeep(draftLayer),
            options: cloneDeep(options),
            sessionGeneration,
        };
        this.store.setRendering(true);

        try {
            const data = draftLayer.data as IImageLayerProps;
            if (options.isEmpty || isEmptyEffect(data)) {
                ImageEffectAction.releasePreviewLease(this.effectPreviewLease);
                this.effectPreviewLease = null;
                if (data.effect) {
                    data.effect.screenshotUrl = '';
                    delete data.effect.effectPosition;
                }
                const cleanLayer = this.cleanRenderFlags(draftLayer);
                if (taskId !== this.renderTaskId || sessionGeneration !== this.sessionGeneration) return;
                await this.getLayerObject(cleanLayer.id)?.setPreviewLayer?.(
                    cleanLayer,
                    this.effectPreviewToken ?? undefined,
                );
                if (taskId !== this.renderTaskId || sessionGeneration !== this.sessionGeneration) return;
                this.store.setIsWasmReady(true);
                this.store.setCurrentLayer(cleanLayer);
                return;
            }

            const previewLayer = this.cleanRenderFlags(draftLayer);
            this.store.setIsWasmReady(false);
            const previewToken = this.effectPreviewToken;
            if (!previewToken) return;
            const previewResult = this.effectPreviewLease
                ? await ImageEffectAction.updatePreviewLease(this.effectPreviewLease, previewLayer)
                : await ImageEffectAction.createPreviewLease(
                      previewLayer,
                      await renderImageBaseLayerCanvas(previewLayer, { globalAlpha: false }),
                      previewToken,
                  );

            if (sessionGeneration !== this.sessionGeneration || previewToken !== this.effectPreviewToken) {
                ImageEffectAction.releasePreviewLease(previewResult.lease);
                return;
            }
            if (taskId !== this.renderTaskId) return;
            this.effectPreviewLease = previewResult.lease;
            const previewEffect = (previewLayer.data as IImageLayerProps).effect;
            if (previewEffect) {
                previewEffect.effectPosition = cloneDeep(previewResult.effectPosition);
            }
            await this.getLayerObject(previewLayer.id)?.setEffectPreviewLayer?.(
                previewToken,
                previewLayer,
                previewResult.canvas,
            );
            if (taskId !== this.renderTaskId || sessionGeneration !== this.sessionGeneration) return;
            this.store.setIsWasmReady(true);
            this.store.setCurrentLayer(previewLayer);
        } finally {
            if (taskId === this.renderTaskId) {
                this.store.setRendering(false);
            }
        }
    }

    async apply() {
        const { originLayer, currentLayer } = this.syncStore();
        if (!originLayer || !currentLayer) return;
        if (this.store.applyLoading) return;

        const sessionGeneration = this.sessionGeneration;
        const latestDraft =
            this.latestDraft?.sessionGeneration === sessionGeneration ? cloneDeep(this.latestDraft) : null;
        const draftLayer = latestDraft?.layer || currentLayer;
        const trackFunc = this.getTrackFunc(draftLayer, latestDraft?.options);
        this.renderTaskId += 1;
        this.store.setRendering(false);
        const pendingApply: IPendingImageEffectApply = {
            canCommit: true,
            originLayerId: originLayer.id,
        };
        this.pendingApplies.set(sessionGeneration, pendingApply);
        this.store.setApplyLoading(true);
        try {
            while (pendingApply.canCommit) {
                const latestLayer = this.framework.service.flatLayer.getLayer(originLayer.id, true);
                if (!latestLayer) return;
                const cleanOriginLayer = this.cleanRenderFlags(cloneDeep(latestLayer));
                const finalLayer = this.mergeDraftIntoLatest(latestLayer, draftLayer);
                if (isEqual(this.cleanEffectArtifact(cleanOriginLayer), this.cleanEffectArtifact(finalLayer))) {
                    this.finishSession(originLayer, sessionGeneration, true);
                    return;
                }

                const finalData = finalLayer.data as IImageLayerProps;
                if (isEmptyEffect(finalData)) {
                    await this.getImageLayer().updateLayerFromLatest(
                        originLayer.id,
                        (latestLayerAtCommit) => {
                            const candidate = this.mergeDraftIntoLatest(latestLayerAtCommit, draftLayer);
                            const candidateData = candidate.data as IImageLayerProps;
                            if (candidateData.effect) {
                                candidateData.effect.screenshotUrl = '';
                                delete candidateData.effect.effectPosition;
                            }
                            if (
                                isEqual(
                                    this.cleanEffectArtifact(latestLayerAtCommit),
                                    this.cleanEffectArtifact(candidate),
                                )
                            ) {
                                return;
                            }
                            return candidate;
                        },
                        {
                            activateLayer: false,
                            canCommit: () => pendingApply.canCommit,
                            trackFunc,
                        },
                    );
                    this.finishSession(originLayer, sessionGeneration, true);
                    return;
                }

                const renderedEffectParams = getEffectParams(finalLayer);
                const baseCanvas = await renderImageBaseLayerCanvas(finalLayer, { globalAlpha: false });
                const effectBase64 = await ImageEffectAction.runEffectAndGetUrl(finalLayer, baseCanvas, true);
                if (!effectBase64) {
                    throw new Error(`Image effect result is empty: ${finalLayer.id}`);
                }
                const effectCode = base64Kit.replaceCode(effectBase64, renderedEffectParams);
                let shouldRetry = false;
                const committedLayer = await this.getImageLayer().updateLayerFromLatest(
                    originLayer.id,
                    (latestLayerAtCommit) => {
                        const candidate = this.mergeDraftIntoLatest(latestLayerAtCommit, draftLayer);
                        if (getEffectParams(candidate) !== renderedEffectParams) {
                            shouldRetry = true;
                            return;
                        }
                        const candidateEffect = (candidate.data as IImageLayerProps).effect;
                        if (!candidateEffect) return;
                        candidateEffect.screenshotUrl = effectCode;
                        // 截图与外扩边界必须来自同一次渲染，避免退出预览后按旧边界拉伸特效。
                        candidateEffect.effectPosition = cloneDeep(finalData.effect?.effectPosition);
                        return candidate;
                    },
                    {
                        activateLayer: false,
                        canCommit: () => pendingApply.canCommit,
                        trackFunc,
                    },
                );
                if (shouldRetry) continue;
                if (!committedLayer) return;
                this.finishSession(originLayer, sessionGeneration, true);
                return;
            }
        } catch (error) {
            this.restoreOriginLayer(originLayer, false, sessionGeneration, pendingApply.canCommit);
            throw error;
        } finally {
            if (this.pendingApplies.get(sessionGeneration) === pendingApply) {
                this.pendingApplies.delete(sessionGeneration);
            }
            if (sessionGeneration === this.sessionGeneration) {
                this.syncStore().setApplyLoading(false);
            }
        }
    }

    /**
     * 取消图片特效会话并恢复原图
     *
     * @param options - 可选；控制恢复后是否重新激活原图
     */
    cancel(options: ICancelImageEffectOptions = {}) {
        const { activateLayer = false } = options;
        const originLayer = this.syncStore().originLayer;
        if (originLayer) {
            const sessionGeneration = this.sessionGeneration;
            const pendingApply = this.pendingApplies.get(sessionGeneration);
            if (pendingApply) {
                pendingApply.canCommit = false;
            }
            this.restoreOriginLayer(originLayer, activateLayer, sessionGeneration, true);
        } else {
            this.releaseEffectPreview(this.effectPreviewLease?.layerId);
            this.sessionGeneration += 1;
            this.renderTaskId += 1;
            this.latestDraft = null;
            this.store.destroy();
            this.releaseFocus();
        }
    }

    /** @returns 当前图片特效草稿图层 */
    getApplyData() {
        return this.store.currentLayer;
    }

    /** 重置经典模糊参数。 */
    resetBlurParams() {
        this.classicBlurParams = cloneDeep(DefaultClassicBlurParams);
    }

    /**
     * 获取清除 GL 临时标记的模糊基准图层
     *
     * @param layer - 图片图层
     * @returns 独立的正式图层快照
     */
    getCleanBlurLayer(layer: ILayerProps) {
        return this.cleanRenderFlags(cloneDeep(layer));
    }

    /**
     * 取消模糊并恢复基准图层
     *
     * @param originLayer - 正在预览模糊的图片图层；仅使用其 ID 定位本地预览
     */
    cancelBlur(originLayer: ILayerProps) {
        const layerId = originLayer.id;
        this.blurRenderTaskId += 1;
        if (this.blurDraft?.layerId === layerId) this.blurDraft = null;
        this.getLayerObject(layerId)?.clearPreviewResource?.();
    }

    /**
     * 渲染经典模糊预览
     *
     * @param originLayer - 模糊基准图层
     * @param alpha - 模糊强度，取值 0-100
     */
    async previewClassicBlur(originLayer: ILayerProps, alpha: number) {
        const taskId = ++this.blurRenderTaskId;
        if (alpha !== undefined) {
            this.classicBlurParams.mFocalScale = alpha / 100;
        }
        await ImageEffectAction.renderClassicBlur(cloneDeep(originLayer), this.classicBlurParams);
        const sourceCanvas = await ImageEffectAction.getImageClassicBlurResultCanvas();
        if (!sourceCanvas || taskId !== this.blurRenderTaskId) return;
        await this.commitBlurPreview(originLayer.id, 'classic', sourceCanvas, taskId);
    }

    /**
     * 渲染玻璃模糊预览
     *
     * @param originLayer - 模糊基准图层
     * @param alpha - 模糊强度
     */
    async previewGlassBlur(originLayer: ILayerProps, alpha: number) {
        const taskId = ++this.blurRenderTaskId;
        await ImageEffectAction.renderImageGlassBlur(cloneDeep(originLayer));
        await ImageEffectAction.changeImageGlassBlurAlpha(alpha);
        const sourceCanvas = await ImageEffectAction.getImageGlassBlurResultCanvas();
        if (!sourceCanvas || taskId !== this.blurRenderTaskId) return;
        await this.commitBlurPreview(originLayer.id, 'glass', sourceCanvas, taskId);
    }

    /**
     * 实时更新经典模糊强度
     *
     * @param alpha - 模糊强度，取值 0-100
     */
    async changeClassicBlurAlpha(alpha: number) {
        const draft = this.blurDraft;
        if (!draft || draft.type !== 'classic') return;
        const taskId = ++this.blurRenderTaskId;
        await ImageEffectAction.changeClassicBlurAlpha(alpha, this.classicBlurParams);
        const sourceCanvas = await ImageEffectAction.getImageClassicBlurResultCanvas();
        if (!sourceCanvas || taskId !== this.blurRenderTaskId) return;
        await this.commitBlurPreview(draft.layerId, 'classic', sourceCanvas, taskId);
    }

    /**
     * 实时更新玻璃模糊强度
     *
     * @param alpha - 模糊强度
     */
    async changeGlassBlurAlpha(alpha: number) {
        const draft = this.blurDraft;
        if (!draft || draft.type !== 'glass') return;
        const taskId = ++this.blurRenderTaskId;
        await ImageEffectAction.changeImageGlassBlurAlpha(alpha);
        const sourceCanvas = await ImageEffectAction.getImageGlassBlurResultCanvas();
        if (!sourceCanvas || taskId !== this.blurRenderTaskId) return;
        await this.commitBlurPreview(draft.layerId, 'glass', sourceCanvas, taskId);
    }

    /**
     * 提交当前模糊结果为图片原图数据
     *
     * @param type - 模糊类型
     * @param activeLayer - 当前图片图层；仅使用其 ID 定位最新正式数据
     * @returns 提交后的独立图层数据
     */
    async applyBlur(type: IImageBlurType, activeLayer: ILayerProps) {
        const layerId = activeLayer.id;
        const draft = this.blurDraft;
        if (!draft || draft.layerId !== layerId || draft.type !== type) return;
        this.blurRenderTaskId += 1;
        const resUrl = await ImageEffectAction.uploadImageResultCanvas(draft.sourceCanvas);
        if (!resUrl) throw new Error(`Image blur result is empty: ${layerId}`);

        const committedLayer = await this.getImageLayer().updateLayerFromLatest(layerId, (latestLayer) => {
            if (this.blurDraft !== draft) return;
            const candidate = this.cleanRenderFlags(cloneDeep(latestLayer));
            (candidate.data as IImageLayerProps).url = resUrl;
            return candidate;
        });
        if (!committedLayer) return;
        if (this.blurDraft === draft) this.blurDraft = null;
        this.getLayerObject(layerId)?.commitAppliedResource?.();
        return committedLayer;
    }

    private async commitBlurPreview(
        layerId: string,
        type: IImageBlurType,
        sourceCanvas: HTMLCanvasElement,
        taskId: number,
    ) {
        await this.getLayerObject(layerId)?.setResource?.(sourceCanvas);
        if (taskId !== this.blurRenderTaskId) return;
        this.blurDraft = { layerId, sourceCanvas, type };
    }

    /**
     * 提取图片图层的可编辑效果数据
     *
     * @param layerId - 来源图片图层 ID
     * @returns 独立的图片效果数据；图层不存在或类型不匹配时返回 undefined
     */
    extractEffectData(layerId: string) {
        const layer = this.framework.service.flatLayer.getLayer(layerId, true);
        if (!layer || layer.layerType !== LayerType.IMAGE_LAYER) return;
        const layerData = layer.data as IImageLayerProps;
        const effect = cloneDeep(layerData.effect);
        if (effect) {
            effect.screenshotUrl = '';
            delete effect.effectPosition;
        }
        this.effectData = {
            effect,
            borderRadius: cloneDeep(layerData.borderRadius),
            opacity: layer.opacity,
        };
        return this.effectData;
    }

    /**
     * 清理当前格式刷缓存
     *
     * @param effectData - 可选；只清理与当前会话相同的数据引用
     */
    clearEffectData(effectData = this.effectData) {
        if (this.effectData === effectData) this.effectData = null;
    }

    /**
     * 开启图片特效格式刷
     *
     * @param layerId - 来源图片图层 ID
     * @param tip - 可选；鼠标提示文案
     * @returns 是否成功开启格式刷会话
     */
    setImageBrush(layerId: string, tip?: string) {
        const implementation: FormatBrushImplementation<ImageBrushEffectData> = {
            targetLayerType: LayerType.IMAGE_LAYER,
            extract: (sourceLayerId) => this.extractEffectData(sourceLayerId),
            apply: (targetLayerId, _context, data) => this.applyImageBrush(targetLayerId, data),
            dispose: (data) => this.clearEffectData(data),
        };
        return this.framework.service.formatBrush.start({
            sourceLayerId: layerId,
            cursorTip: tip,
            implementation,
        });
    }

    private applyImageBrush(targetLayerId: string, effectData: ImageBrushEffectData) {
        return applyImageBrushEffect(this.framework, this.getImageLayer(), targetLayerId, effectData);
    }
}
